/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useRef, useState } from "react";
import "../../../../css/LiveDisplay.css";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { hostName } from "../../../../App";
import { ClapSpinner } from "react-spinners-kit";
import Icon from "react-icons-kit";
import { settings } from "react-icons-kit/feather/settings";
import { maximize } from "react-icons-kit/feather/maximize";
import { useSearchParams } from "react-router-dom";
import i18next from "i18next";
import SettingsDrawer from "../live-display/SettingsDrawer";
import PledgedProgressPercentage from "./PledgedProgressPercentage";

const LiveDisplay = ({ tagNumber }) => {
  // t function
  const { t } = useTranslation();

  // settings
  const defaultSettings = {
    theme: "Color",
    bgColor: "#252142",
    bgGradient: "sunset-glow",
    bgImage: "",
    qrImage: "",
    bannerImage: "",
    dontDisplayParticles: false,
    dontDisplayAnimation: false,
    particleShape: "✦",
    animationStyle: "floating-balls",
    language: "en",
  };

  const [displaySettings, setDisplaySettings] = useState(() => {
    const stored = localStorage.getItem("displaySettings");

    if (!stored) {
      localStorage.setItem("displaySettings", JSON.stringify(defaultSettings));
      return defaultSettings;
    }

    try {
      const parsed = JSON.parse(stored);

      const isNewKeyInDefaults = Object.keys(defaultSettings).some(
        (key) => !(key in parsed)
      );

      const hasObsoleteKeys = Object.keys(parsed).some(
        (key) => !(key in defaultSettings)
      );

      if (isNewKeyInDefaults || hasObsoleteKeys) {
        localStorage.setItem(
          "displaySettings",
          JSON.stringify(defaultSettings)
        );
        return defaultSettings;
      }

      return parsed;
    } catch (e) {
      console.error("Failed to parse displaySettings from localStorage:", e);
      localStorage.setItem("displaySettings", JSON.stringify(defaultSettings));
      return defaultSettings;
    }
  });

  // Sync any changes to displaySettings to localStorage
  useEffect(() => {
    localStorage.setItem("displaySettings", JSON.stringify(displaySettings));
  }, [displaySettings]);

  // api states
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  // countdown timer
  const [countdown, setCountdown] = useState(300);

  // fetch api
  useEffect(() => {
    let timer = 300;
    let intervalId;

    const fetchData = () => {
      axios
        .get(
          `${hostName}api/v1/liveDisplay/pledge/control?tagNumber=${tagNumber}`
          // {
          //   headers: {
          //     "X-Local-Access-Token":
          //       "zrPIG1Q90I0cfPhmBZuH1hQErA1mUvQG7mE4jk3ta8lqBJw1L15TGvqJD5MMxwAO",
          //   },
          // }
        )
        .then((res) => {
          const newData = res.data?.data;

          if (newData) {
            setData((prev) => {
              if (!prev) return newData;

              const updated = { ...prev };
              let changed = false;

              Object.keys(newData).forEach((key) => {
                if (prev[key] !== newData[key]) {
                  updated[key] = newData[key];
                  changed = true;
                }
              });

              return changed ? updated : prev;
            });

            // update bg image in displaySettings, it will be automatically added to LS
            if (newData.liveDisplayImageName) {
              if (displaySettings.bgImage !== newData.liveDisplayImage) {
                setDisplaySettings((prev) => ({
                  ...prev,
                  bgImage: newData.liveDisplayImage,
                }));
              }
            } else {
              setDisplaySettings((prev) => ({
                ...prev,
                bgImage: "",
              }));
            }

            // update banner image in displaySettings, it will be automatically added to LS
            if (newData.bannerImageName) {
              if (displaySettings.bannerImage !== newData.bannerImage) {
                setDisplaySettings((prev) => ({
                  ...prev,
                  bannerImage: newData.bannerImage,
                }));
              }
            } else {
              setDisplaySettings((prev) => ({
                ...prev,
                bannerImage: "",
              }));
            }

            // update qr image in displaySettings, it will be automatically added to LS
            if (newData.qrCode) {
              if (displaySettings.qrImage !== newData.qrCode) {
                setDisplaySettings((prev) => ({
                  ...prev,
                  qrImage: newData.qrCode,
                }));
              }
            } else {
              setDisplaySettings((prev) => ({
                ...prev,
                qrImage: "",
              }));
            }

            if (newData.showProgress && !intervalId) {
              intervalId = setInterval(() => {
                timer -= 1;
                setCountdown(timer);

                if (timer <= 0) {
                  fetchData();
                  timer = 300;
                  setCountdown(300);
                }
              }, 1000);
            }
          } else {
            setError("No data field found in response");
          }
        })
        .catch((err) => {
          let errorMessage = "An unexpected error occurred. Please try again.";
          if (err.response) {
            errorMessage =
              err.response.data?.message || "Server error occurred.";
          } else if (err.request) {
            errorMessage =
              "No response from server. Please check your connection.";
          } else {
            errorMessage = err.message;
          }
          setError(errorMessage);
        })
        .finally(() => setLoading(false));
    };

    fetchData();

    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [tagNumber]);

  // icon size
  const [iconSize, setIconSize] = useState(30);

  useEffect(() => {
    // Function to calculate the responsive font size
    const updateIconSize = () => {
      const width = window.innerWidth;
      const height = window.innerHeight;
      // Adjust the size dynamically based on the screen width and height
      setIconSize(Math.max(15, (width + height) * 0.0125, 20)); // You can adjust this multiplier
    };

    // Update icon size on window resize
    window.addEventListener("resize", updateIconSize);

    // Call the function once to set initial size
    updateIconSize();

    // Cleanup event listener
    return () => {
      window.removeEventListener("resize", updateIconSize);
    };
  }, []);

  // settings
  const [settingsDrawer, setSettingsDrawer] = useState(false);

  const handleSettingsDrawer = () => {
    setSettingsDrawer(true);
  };

  // full screen
  const goInFullScreenMode = () => {
    if (
      document.fullscreenElement ||
      document.webkitFullscreenElement ||
      document.msFullscreenElement
    ) {
      // Already in fullscreen, so exit
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen();
      } else if (document.msExitFullscreen) {
        document.msExitFullscreen();
      }
    } else {
      // Not in fullscreen, so enter
      const elem = document.documentElement;
      if (elem.requestFullscreen) {
        elem.requestFullscreen();
      } else if (elem.webkitRequestFullscreen) {
        elem.webkitRequestFullscreen();
      } else if (elem.msRequestFullscreen) {
        elem.msRequestFullscreen();
      }
    }
  };

  // buttons
  const [showButtons, setShowButtons] = useState(false);
  const [timeoutId, setTimeoutId] = useState(null);

  const handleMouseMove = () => {
    setShowButtons(true);

    if (timeoutId) clearTimeout(timeoutId);

    const newTimeout = setTimeout(() => {
      setShowButtons(false);
    }, 2000);

    setTimeoutId(newTimeout);
  };

  // donor feed
  // api states
  const [feedLoading, setFeedLoading] = useState(true);
  const [feedData, setFeedData] = useState(null);

  // donor feed timer
  const [donorFeedTimer, setDonorFeedTimer] = useState(300);

  useEffect(() => {
    if (!data?.showDonorFeed) return; // exit early if false or undefined

    setFeedLoading(true);

    let timer = 300;
    let intervalId;

    const fetchDonorFeed = () => {
      axios
        .get(
          `${hostName}api/v1/liveDisplay/pledge/donorFeed?tagNumber=${tagNumber}`
          // {
          //   headers: {
          //     "X-Local-Access-Token":
          //       "zrPIG1Q90I0cfPhmBZuH1hQErA1mUvQG7mE4jk3ta8lqBJw1L15TGvqJD5MMxwAO",
          //   },
          // }
        )
        .then((res) => {
          const newData = res.data?.data;

          if (newData) {
            // Check if the new data is different from previous data before updating
            setFeedData((prev) => {
              if (!prev) return newData;

              // Simple deep comparison: check length and shallow equality of objects at same indices
              if (
                prev.length !== newData.length ||
                newData.some(
                  (item, index) =>
                    JSON.stringify(item) !== JSON.stringify(prev[index])
                )
              ) {
                return newData;
              }
              return prev; // no changes
            });
          } else {
            console.error("No data field found in response");
          }
        })
        .catch((err) => {
          let errorMessage = "An unexpected error occurred. Please try again.";

          if (err.response) {
            errorMessage =
              err.response.data?.message || "Server error occurred.";
          } else if (err.request) {
            errorMessage =
              "No response from server. Please check your connection.";
          } else {
            errorMessage = err.message;
          }

          console.error(errorMessage);
        })
        .finally(() => setFeedLoading(false));
    };

    fetchDonorFeed();

    // Start 1-second interval timer to count down and refresh every 5 minutes
    intervalId = setInterval(() => {
      timer -= 1;
      setDonorFeedTimer(timer);

      if (timer <= 0) {
        fetchDonorFeed();
        timer = 300;
        setDonorFeedTimer(300);
      }
    }, 1000);

    // Cleanup interval on unmount or tagNumber change
    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [data?.showDonorFeed, tagNumber]);

  // lang code
  // search params
  const [searchParams, setSearchParams] = useSearchParams();

  // Get language from query param
  useEffect(() => {
    i18next.changeLanguage(displaySettings.language);
    const queryLang = searchParams.get("language");

    if (queryLang) {
      setDisplaySettings((prev) => ({
        ...prev,
        language: queryLang,
      }));
      i18next.changeLanguage(queryLang);
    }
  }, []);

  // handle language change on button click
  const handleLanguage = (id) => {
    setDisplaySettings((prev) => ({
      ...prev,
      language: id,
    }));
    i18next.changeLanguage(id);

    // Update query parameter dynamically
    searchParams.set("language", id);
    setSearchParams(searchParams);
  };

  // particles container
  const particlesContainer = useRef(null);

  // create particles
  useEffect(() => {
    if (
      data &&
      particlesContainer?.current &&
      displaySettings.dontDisplayParticles === false
    ) {
      const shape = displaySettings.particleShape;

      const colors = [
        "#FF6B6B",
        "#FFD93D",
        "#6BCB77",
        "#4D96FF",
        "#F72585",
        "#A66CFF",
      ];

      const createParticle = () => {
        // 4 particles per 100ms || 40 particles per second
        for (let i = 0; i < 4; i++) {
          const particle = document.createElement("span");
          particle.className = "particle";
          particle.innerText = shape;

          particle.style.left = Math.random() * 100 + "vw"; // randomly place each particle on screen
          particle.style.color =
            colors[Math.floor(Math.random() * colors.length)];
          // Responsive font size between ~1.25vw to ~2.25vw range
          const min = 1.25; // vw
          const max = 2.25; // vw
          const size = min + Math.random() * (max - min);
          particle.style.fontSize = `calc(${size}vw + ${size * 0.5}vh)`;
          particle.style.animationDuration = 3 + Math.random() * 3 + "s"; // animation is 3s - 6s

          particlesContainer.current.appendChild(particle);

          setTimeout(() => {
            particle.remove();
          }, 6000); // remove every particle after 6 seconds
        }
      };

      const interval = setInterval(createParticle, 100); // create 4 particles every 100ms, or 40 particles every second

      return () => clearInterval(interval);
    }
  }, [data, displaySettings]);

  // progress bar / gauge
  const fillRef = useRef(null);
  const coverRef = useRef(null);

  useEffect(() => {
    if (data?.pledgedPercentage) {
      const normalized =
        Math.min(Math.max(data?.pledgedPercentage, 0), 100) / 100; // Clamp and normalize

      if (fillRef.current) {
        fillRef.current.style.transform = `rotate(${normalized / 2}turn)`;
      }

      if (coverRef.current) {
        coverRef.current.textContent = `${Math.min(
          data?.pledgedPercentage,
          100
        )}%`;
      }
    }
  }, [data?.pledgedPercentage]);

  return (
    <>
      <div
        ref={particlesContainer}
        style={{
          background:
            loading || error || !data
              ? "#fff"
              : displaySettings.theme === "Color"
              ? displaySettings.bgColor
              : undefined,
          transition: "background 0.5s ease",
          justifyContent: loading || error || !data ? "center" : undefined,
          ...(displaySettings.theme === "Image" && data?.liveDisplayImage
            ? { "--bg-image": `url(${displaySettings.bgImage})` }
            : {}),
        }}
        className={[
          "live-display-wrapper",
          !(loading || error || !data) &&
            displaySettings.theme === "Gradient" &&
            displaySettings.bgGradient,
          !(loading || error || !data) &&
            displaySettings.dontDisplayAnimation === false &&
            displaySettings.animationStyle,
          !(loading || error || !data) &&
            displaySettings.theme === "Image" &&
            "bg-img",
        ]
          .filter(Boolean)
          .join(" ")}
      >
        {loading ? (
          <ClapSpinner loading={loading} color="#007c16" size={iconSize} />
        ) : (
          <>
            {error ? (
              <div
                className="error-msg"
                style={{ marginTop: 0, fontSize: "1vw" }}
              >
                {error}
              </div>
            ) : (
              <>
                {!data ? (
                  <div
                    className="error-msg"
                    style={{ marginTop: 0, fontSize: "1vw" }}
                  >
                    No data found for pledge {tagNumber}
                  </div>
                ) : (
                  <>
                    {/* background animations */}
                    {displaySettings.dontDisplayAnimation === false && (
                      <>
                        {/* floating-balls */}
                        {displaySettings.animationStyle ===
                          "floating-balls" && (
                          <>
                            {Array.from({ length: 25 }).map((_, i) => (
                              <span key={i} className="ball"></span>
                            ))}
                          </>
                        )}

                        {/* color-drip */}
                        {displaySettings.animationStyle === "color-drip" && (
                          <>
                            {Array.from({ length: 10 }).map((_, i) => (
                              <div key={i} className="line"></div>
                            ))}
                          </>
                        )}

                        {/* shooting stars */}
                        {displaySettings.animationStyle ===
                          "shooting-stars" && (
                          <>
                            <div className="stars"></div>
                            {Array.from({ length: 5 }).map((_, i) => (
                              <div key={i} className="shooting-star"></div>
                            ))}
                          </>
                        )}
                      </>
                    )}

                    {/* pledge name */}
                    <h1 className="pledge-name">{data.name}</h1>

                    {/* pledged-progress-percentage */}
                    {data.showProgress && (
                      <PledgedProgressPercentage
                        pledgedPercentage={data.pledgedPercentage}
                      />
                    )}

                    {/* banner, buttons, and progress bar*/}
                    <div
                      className="display-banner-div"
                      onMouseMove={() => handleMouseMove()}
                      onMouseEnter={() => setShowButtons(true)}
                    >
                      {/* banner */}
                      {displaySettings.bannerImage && (
                        <img
                          src={displaySettings.bannerImage}
                          alt="Pledge Banner"
                        />
                      )}

                      {/* Buttons */}
                      <div
                        className={`banner-buttons ${
                          showButtons ? "show" : "hide"
                        }`}
                      >
                        <button
                          className="banner-btn"
                          onClick={goInFullScreenMode}
                        >
                          <Icon icon={maximize} size={iconSize} />{" "}
                          <span>{t("LiveDisplay.full_screen")}</span>
                        </button>
                        <button
                          className="banner-btn"
                          onClick={handleSettingsDrawer}
                        >
                          <Icon icon={settings} size={iconSize} />{" "}
                          <span>{t("LiveDisplay.settings")}</span>
                        </button>
                      </div>
                    </div>

                    {/* qr code */}
                    {displaySettings.qrImage && (
                      <div className="qr-code-div">
                        <img src={displaySettings.qrImage} alt="qrCode" />
                      </div>
                    )}

                    {/* powered by */}
                    <div className="powered-by-div">
                      {data.showDonorFeed && (
                        <div className="donor-feed">
                          {feedLoading ? (
                            <p style={{ fontSize: "1.1vw" }}>
                              {t("LiveDisplay.please_wait")}
                            </p>
                          ) : (
                            feedData?.length > 0 && (
                              <div
                                className={`donor-feed-vertical-wrapper ${
                                  feedData.length >= 6 ? "gradient" : ""
                                }`}
                              >
                                <div
                                  className={`donor-feed-vertical-scroll ${
                                    feedData.length >= 6 ? "scrolling" : ""
                                  }`}
                                  style={{
                                    "--total-items": feedData.length,
                                  }}
                                >
                                  {(feedData.length >= 6
                                    ? feedData.concat(feedData)
                                    : feedData
                                  ).map((donor, index) => (
                                    <div className="donor-item" key={index}>
                                      <span className="donor-name">
                                        {donor.donor}
                                      </span>
                                      <span className="donor-amount">
                                        {donor.amount > 0
                                          ? `${donor.currencySymbol}${donor.amount}`
                                          : ""}
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )
                          )}
                        </div>
                      )}
                      <h4>
                        {t("LiveDisplay.powered_by")}{" "}
                        <img
                          src="https://donatenowws.mervice.ca/resources/images/donorchoicewhite.png"
                          alt="Donor Choice"
                        />
                      </h4>
                    </div>
                  </>
                )}
              </>
            )}
          </>
        )}
      </div>

      {/* settings drawer */}
      {settingsDrawer && (
        <SettingsDrawer
          setSettingsDrawer={setSettingsDrawer}
          displaySettings={displaySettings}
          setDisplaySettings={setDisplaySettings}
          handleLanguage={handleLanguage}
          data={data}
          countdown={countdown}
          donorFeedTimer={donorFeedTimer}
        />
      )}
    </>
  );
};

export default LiveDisplay;
